import { AggregationPeriod, Button, DetectionZoneCardinalDirection, EventReports, Facilities, Facility, LaneDirectionJson, LaneLine, TurningMovementReportConfiguration, TurnType, Vector2 } from "@app/shared";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFetch } from "../../hooks";
import { calculateCardinalDirection, useFacilityCoordsHelper, getCardinalDirectionAbbreviation, getCardinalDirectionWithBound, detectLaneLineStreetName } from "../../helpers";
import { IconLink, Icons, Select } from "../shared";
import { DateOptions, IConfiguredTrafficReport, TrafficReportProps } from "./IConfiguredTrafficReport";
import {
    lightFhwaClasses,
    mediumFhwaClasses,
    heavyFhwaClasses,
    getLaneNumberColor,
    getIntersectionName,
    formatPeakHourLabel,
    calculateRollingPeakHourGeneric,
    RollingPeakHourResult,
} from "./TurningMovementReport/TmcHelpers";
import { ReportContext } from "./reportHelpers";
import { IntersectionDiagram } from "./IntersectionDiagram";
import { OmniGoogleMap, OmniMapMethods } from "../shared/maps/OmniGoogleMap";
import { LaneLineMapShape } from "../shared/maps/MapShapes/LaneLineMapShape";
import { DynamicFixedSizeContainer } from "../shared/DynamicFixedSizeContainer";

interface V2PeakHourResult {
    peakStartTime: Date | null;
    peakEndTime: Date | null;
    peakTotal: number;
    peakTotals: Map<string, number>;
    peakRows: any[];
}

const calculateRollingPeakHour = (
    dayRows: any[],
    columns: any[],
    getCountForColumn: (row: any, columnKey: string) => number
): V2PeakHourResult => {
    const emptyResult: V2PeakHourResult = {
        peakStartTime: null,
        peakEndTime: null,
        peakTotal: 0,
        peakTotals: new Map(),
        peakRows: [],
    };

    if (!dayRows || dayRows.length === 0 || !columns || columns.length === 0) {
        return emptyResult;
    }

    const genericResult = calculateRollingPeakHourGeneric(
        dayRows,
        (row) => (row.startTime ? new Date(row.startTime) : null),
        (row) => columns.reduce((sum: number, col: any) => sum + getCountForColumn(row, col.key), 0)
    );

    if (genericResult.peakStartIndex < 0) {
        return emptyResult;
    }

    const peakTotals = new Map<string, number>();
    columns.forEach((col: any) => {
        const colTotal = genericResult.peakRows.reduce((sum: number, row: any) => sum + getCountForColumn(row, col.key), 0);
        peakTotals.set(col.key, colTotal);
    });

    return {
        peakStartTime: genericResult.peakStartTime,
        peakEndTime: genericResult.peakEndTime,
        peakTotal: genericResult.peakTotal,
        peakTotals,
        peakRows: genericResult.peakRows,
    };
};

const downloadCSVV2 = (reportData: any, directionStreetNames: Map<number, string>, vehicleTypeLabel: string, startDate: Date, endDate: Date) => {
    if (!reportData?.counts || reportData.counts.length === 0) return;

    try {
        const columns = reportData.columns || [];
        const columnsByDirection = new Map<number, any[]>();
        columns.forEach((col: any) => {
            if (!columnsByDirection.has(col.direction)) {
                columnsByDirection.set(col.direction, []);
            }
            columnsByDirection.get(col.direction)!.push(col);
        });

        const dataByDay = new Map<string, any[]>();
        reportData.counts.forEach((row: any) => {
            if (row.startTime) {
                const date = new Date(row.startTime);
                const dayKey = date.toDateString();
                if (!dataByDay.has(dayKey)) {
                    dataByDay.set(dayKey, []);
                }
                dataByDay.get(dayKey)!.push(row);
            }
        });

        let csv = "";

        dataByDay.forEach((dayRows, dayKey) => {
            csv += `Traffic Counts - ${vehicleTypeLabel},`;
            columns.forEach(() => (csv += ","));
            csv += "\n";

            csv += ",";
            Array.from(columnsByDirection.entries()).forEach(([dir, cols]) => {
                const streetName = directionStreetNames.get(dir) || "";
                csv += streetName;
                cols.forEach(() => (csv += ","));
            });
            csv += "\n";

            csv += ",";
            Array.from(columnsByDirection.entries()).forEach(([dir, cols]) => {
                cols.forEach((col: any) => (csv += col.displayLabel + ","));
            });
            csv += "Total\n";

            dayRows.forEach((row: any) => {
                const time = row.startTime ? new Date(row.startTime).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : "";
                csv += time + ",";

                let rowTotal = 0;
                columns.forEach((col: any) => {
                    const countObj = row.counts?.find((c: any) => c.columnKey === col.key);
                    const count = countObj?.count || 0;
                    rowTotal += count;
                    csv += count + ",";
                });
                csv += rowTotal + "\n";
            });

            csv += "Total,";
            let grandTotal = 0;
            columns.forEach((col: any) => {
                const colTotal = dayRows.reduce((sum: number, row: any) => {
                    const countObj = row.counts?.find((c: any) => c.columnKey === col.key);
                    return sum + (countObj?.count || 0);
                }, 0);
                grandTotal += colTotal;
                csv += colTotal + ",";
            });
            csv += grandTotal + "\n";

            const getCountForColumnCSV = (row: any, columnKey: string) => {
                const countObj = row.counts?.find((c: any) => c.columnKey === columnKey);
                return countObj?.count || 0;
            };
            const peakHourResult = calculateRollingPeakHour(dayRows, columns, getCountForColumnCSV);

            if (peakHourResult.peakStartTime) {
                const peakTimeLabel = formatPeakHourLabel(peakHourResult.peakStartTime, peakHourResult.peakEndTime);
                csv += `Peak Hour (${peakTimeLabel}),`;
                columns.forEach((col: any) => {
                    csv += (peakHourResult.peakTotals.get(col.key) || 0) + ",";
                });
                csv += peakHourResult.peakTotal + "\n";
            }

            csv += "\n\n";
        });

        const blob = new Blob([csv], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "TurningMovementReport.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error("Download failed:", error);
    }
};

interface LaneLineWithDirection {
    laneLine: LaneLine;
    inferredDirection: DetectionZoneCardinalDirection;
    laneNumber: number; // lane number from layout
}

// group by unique combination of (lane #, direction, turn type)
interface GroupedLaneLine {
    laneNumber: number;
    direction: DetectionZoneCardinalDirection;
    turnType: TurnType | undefined | null;
    laneLines: LaneLine[];
    displayName: string;
    detectedStreetName?: string;
}

interface DirectionGroup {
    direction: DetectionZoneCardinalDirection;
    groupedLaneLines: GroupedLaneLine[];
}

type VehicleType = "all" | "heavy" | "medium" | "light";

export const TMCReportLoader: React.FC<TrafficReportProps> = (props: TrafficReportProps) => {
    const { reportConfig } = useContext(ReportContext);
    const allReady = props && (props.size.height ?? 0) > 10;

    // Fetch facility if not in context but we have a device with facilityId
    const [facility, isFacilityLoading] = useFetch(async () => {
        if (reportConfig.context.facility) {
            return reportConfig.context.facility;
        }
        if (reportConfig.context.device?.facilityId) {
            return await Facilities.getById(reportConfig.context.device.facilityId);
        }
        return undefined;
    }, [reportConfig.context.facility?.id, reportConfig.context.device?.facilityId]);

    if (!allReady || isFacilityLoading) {
        return <div className="p-4">Loading...</div>;
    }

    if (!facility) {
        return <div className="p-4">No facility found for this device.</div>;
    }

    // Pass the loaded facility as a prop
    return <TMCReport {...props} loadedFacility={facility} />;
};

interface TMCReportProps extends TrafficReportProps {
    loadedFacility: Facility;
}

export const TMCReport: React.FC<TMCReportProps> = (props) => {
    const { reportConfig } = useContext(ReportContext);
    const [selectedLaneLineIds, setSelectedLaneLineIds] = useState<Set<string>>(new Set());
    const [conflictResolutions, setConflictResolutions] = useState<Map<number, DetectionZoneCardinalDirection>>(new Map());
    const [vehicleType, setVehicleType] = useState<VehicleType>("all");
    const [reportData, setReportData] = useState<any>(null);
    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [reportError, setReportError] = useState<string | null>(null);
    const [showStreetNames, setShowStreetNames] = useState(false);
    const [isDetectingStreetNames, setIsDetectingStreetNames] = useState(false);
    const [streetNamesDetected, setStreetNamesDetected] = useState(false);
    const [selectedHourByDay, setSelectedHourByDay] = useState<Map<string, number>>(new Map());
    // Tracks whether a report has been generated, so a date-range change can
    // auto-refetch without firing on first mount or while still in setup.
    const reportGeneratedRef = useRef(false);
    const mapRef = useRef<OmniMapMethods>(null);

    // Use the facility passed from the loader (guaranteed to be loaded)
    const facility = props.loadedFacility;

    const [facilityLaneLines, isLaneLinesLoading] = useFetch(async () => {
        if (facility?.laneLines) return facility.laneLines;
        if (!facility?.id) return [];
        const fetchedFacility = await Facilities.getById(facility.id);
        return fetchedFacility?.laneLines || [];
    }, [facility?.id]);

    const laneLines = facilityLaneLines || [];
    const coordsHelper = useFacilityCoordsHelper(facility);

    const directionGroups = useMemo<DirectionGroup[]>(() => {
        if (!laneLines || !coordsHelper) return [];

        const laneLinesWithDirection: LaneLineWithDirection[] = laneLines
            .filter((ll) => ll.points && ll.points.length >= 2)
            .map((ll) => {
                const direction = calculateCardinalDirection(ll.points![0], ll.points![1], coordsHelper);
                return {
                    laneLine: ll,
                    inferredDirection: direction,
                    laneNumber: ll.laneNumber || 0,
                };
            });

        const byDirection = new Map<DetectionZoneCardinalDirection, LaneLineWithDirection[]>();
        laneLinesWithDirection.forEach((llwd) => {
            if (!byDirection.has(llwd.inferredDirection)) {
                byDirection.set(llwd.inferredDirection, []);
            }
            byDirection.get(llwd.inferredDirection)!.push(llwd);
        });

        const result: DirectionGroup[] = [];
        byDirection.forEach((laneLines, direction) => {
            const combos = new Map<string, GroupedLaneLine>();

            laneLines.forEach((llwd) => {
                const key = `${llwd.laneNumber}-${llwd.laneLine.turnType ?? "null"}`;

                if (!combos.has(key)) {
                    const displayName = llwd.laneLine.name || "Unnamed";

                    combos.set(key, {
                        laneNumber: llwd.laneNumber,
                        direction: direction,
                        turnType: llwd.laneLine.turnType,
                        laneLines: [],
                        displayName: displayName,
                    });
                }

                combos.get(key)!.laneLines.push(llwd.laneLine);
            });

            const groupedLaneLines = Array.from(combos.values()).sort((a, b) => {
                if (a.laneNumber === 0 && b.laneNumber !== 0) return 1;
                if (a.laneNumber !== 0 && b.laneNumber === 0) return -1;
                if (a.laneNumber !== b.laneNumber) return a.laneNumber - b.laneNumber;

                const aTurn = a.turnType ?? -1;
                const bTurn = b.turnType ?? -1;
                return aTurn - bTurn;
            });

            result.push({ direction, groupedLaneLines });
        });

        result.sort((a, b) => a.direction - b.direction);

        return result;
    }, [laneLines, coordsHelper]);

    const validation = useMemo(() => {
        const laneNumberToDirections: Map<number, Set<DetectionZoneCardinalDirection>> = new Map();
        const unassignedGroups: GroupedLaneLine[] = [];

        directionGroups.forEach((dirGroup) => {
            dirGroup.groupedLaneLines.forEach((group) => {
                if (group.laneNumber === 0) {
                    unassignedGroups.push(group);
                } else {
                    if (!laneNumberToDirections.has(group.laneNumber)) {
                        laneNumberToDirections.set(group.laneNumber, new Set());
                    }
                    laneNumberToDirections.get(group.laneNumber)!.add(dirGroup.direction);
                }
            });
        });

        const actualConflicts = new Map<number, string[]>();
        const conflictDirectionOptions = new Map<number, DetectionZoneCardinalDirection[]>();

        laneNumberToDirections.forEach((directions, laneNum) => {
            if (directions.size > 1) {
                const directionNames = Array.from(directions).map((dir) => getCardinalDirectionAbbreviation(dir) || "Unknown");
                actualConflicts.set(laneNum, directionNames);
                conflictDirectionOptions.set(laneNum, Array.from(directions));
            }
        });

        return {
            hasConflicts: actualConflicts.size > 0,
            conflicts: actualConflicts,
            conflictDirectionOptions,
            hasUnassigned: unassignedGroups.length > 0,
            unassignedGroups,
        };
    }, [directionGroups]);

    const detectStreetNamesForGroups = async () => {
        if (!coordsHelper || directionGroups.length === 0) return;

        setIsDetectingStreetNames(true);
        try {
            for (const dirGroup of directionGroups) {
                for (const group of dirGroup.groupedLaneLines) {
                    if (group.laneLines.length === 0) continue;

                    const laneLine = group.laneLines[0];
                    const detectedInfo = await detectLaneLineStreetName(laneLine, dirGroup.direction, coordsHelper);

                    if (detectedInfo) {
                        group.detectedStreetName = detectedInfo.formattedName;
                    }
                }
            }
            setStreetNamesDetected(true);
        } catch (error) {
            console.error("Error detecting street names:", error);
        } finally {
            setIsDetectingStreetNames(false);
        }
    };

    useEffect(() => {
        if (showStreetNames && !isDetectingStreetNames) {
            detectStreetNamesForGroups();
        }
    }, [showStreetNames]);

    useEffect(() => {
        if (directionGroups.length > 0 && selectedLaneLineIds.size === 0) {
            const allIds = new Set<string>();
            directionGroups.forEach((dirGroup) => {
                dirGroup.groupedLaneLines.forEach((group) => {
                    group.laneLines.forEach((ll) => {
                        if (ll.id) allIds.add(ll.id);
                    });
                });
            });
            setSelectedLaneLineIds(allIds);
        }
    }, [directionGroups, selectedLaneLineIds.size]);

    // Once a report exists, changing the date range (preset or custom) refetches
    // in place with the same lane selection. (fetchReport is defined below; the
    // reference resolves at effect-run time, after render.)
    useEffect(() => {
        if (!reportGeneratedRef.current) return;
        void fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportConfig.inclusiveStart?.getTime(), reportConfig.exclusiveEnd?.getTime()]);

    if (isLaneLinesLoading) {
        return <div className="p-4">Loading lane lines...</div>;
    }

    const toggleGroup = (group: GroupedLaneLine) => {
        const newSet = new Set(selectedLaneLineIds);
        const groupIds = group.laneLines.map((ll) => ll.id).filter((id) => id) as string[];
        const allSelected = groupIds.every((id) => newSet.has(id));

        if (allSelected) {
            groupIds.forEach((id) => newSet.delete(id));
        } else {
            groupIds.forEach((id) => newSet.add(id));
        }

        setSelectedLaneLineIds(newSet);
    };

    const toggleDirection = (direction: DetectionZoneCardinalDirection) => {
        const dirGroup = directionGroups.find((g) => g.direction === direction);
        if (!dirGroup) return;

        const newSet = new Set(selectedLaneLineIds);
        const allIds: string[] = [];
        dirGroup.groupedLaneLines.forEach((group) => {
            group.laneLines.forEach((ll) => {
                if (ll.id) allIds.push(ll.id);
            });
        });

        const allSelected = allIds.every((id) => newSet.has(id));

        if (allSelected) {
            allIds.forEach((id) => newSet.delete(id));
        } else {
            allIds.forEach((id) => newSet.add(id));
        }

        setSelectedLaneLineIds(newSet);
    };

    // Runs the API call against the currently selected lanes and date range.
    // No street-name detection here — that happens once in handleGenerateReport.
    const fetchReport = async () => {
        setIsLoadingReport(true);
        setReportError(null);

        try {
            const laneDirections: LaneDirectionJson[] = [];
            directionGroups.forEach((dirGroup) => {
                dirGroup.groupedLaneLines.forEach((group) => {
                    const hasSelectedLanes = group.laneLines.some((ll) => ll.id && selectedLaneLineIds.has(ll.id));

                    if (hasSelectedLanes && group.laneNumber !== 0) {
                        laneDirections.push({
                            lane: group.laneNumber,
                            direction: dirGroup.direction,
                            turnType: group.turnType ?? undefined,
                        });
                    }
                });
            });

            if (laneDirections.length === 0) {
                setReportError("Please select at least one lane to generate the report");
                return;
            }

            let classMask: number | undefined;
            if (vehicleType === "heavy") {
                classMask = heavyFhwaClasses;
            } else if (vehicleType === "medium") {
                classMask = mediumFhwaClasses;
            } else if (vehicleType === "light") {
                classMask = lightFhwaClasses;
            }

            const deviceIds: string[] = reportConfig.deviceIds && reportConfig.deviceIds.length > 0 ? reportConfig.deviceIds : reportConfig.context.device?.id ? [reportConfig.context.device.id] : [];
            const facilityIds: string[] = facility?.id ? [facility.id] : [];
            const config: TurningMovementReportConfiguration = {
                deviceIds: deviceIds,
                facilityIds: facilityIds,
                inclusiveStart: reportConfig.inclusiveStart!,
                exclusiveEnd: reportConfig.exclusiveEnd!,
                lanes: laneDirections,
                streetNames: [],
                filterTypes: undefined,
                filterClasses: classMask,
                grouping: 0,
                groupingValues: [],
            };

            const result = await EventReports.getTurningMovementCountsV2(AggregationPeriod.FiveMinutes, config);
            setReportData(result);
            reportGeneratedRef.current = true;
        } catch (error: any) {
            console.error("Error generating report:", error);
            setReportError(error.message || "Failed to generate report");
        } finally {
            setIsLoadingReport(false);
        }
    };

    const handleGenerateReport = async () => {
        if (!streetNamesDetected) await detectStreetNamesForGroups();
        await fetchReport();
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            const scrollableParent = document.querySelector('.print-area')?.closest('[class*="overflow"]');
            if (scrollableParent) scrollableParent.scrollTop = 0;
        }, 100);
    };

    if (laneLines.length === 0) {
        return (
            <div className="p-4">
                <div className="border rounded p-4">
                    <p>No lane lines found for this facility. Please create lane lines first.</p>
                </div>
            </div>
        );
    }

    if (reportData && !isLoadingReport) {
        const getCountForColumn = (row: any, columnKey: string) => {
            const countObj = row.counts?.find((c: any) => c.columnKey === columnKey);
            return countObj?.count || 0;
        };

        const columnsByDirection = new Map<number, any[]>();
        reportData.columns?.forEach((col: any) => {
            if (!columnsByDirection.has(col.direction)) {
                columnsByDirection.set(col.direction, []);
            }
            columnsByDirection.get(col.direction)!.push(col);
        });

        const directionStreetNames = new Map<number, string>();

        directionGroups.forEach((dirGroup) => {
            const firstLaneWithStreet = dirGroup.groupedLaneLines.find((group) => group.detectedStreetName && group.laneLines.length > 0);
            if (firstLaneWithStreet && firstLaneWithStreet.detectedStreetName) {
                let streetName: string | null = null;

                let match = firstLaneWithStreet.detectedStreetName.match(/\(.*?\)\s+(.+?)\s+-\s+/);
                if (match && match[1]) {
                    streetName = match[1];
                }

                if (!streetName) {
                    match = firstLaneWithStreet.detectedStreetName.match(/^(.+?)\s+-\s+/);
                    if (match && match[1]) {
                        streetName = match[1];
                    }
                }

                if (!streetName && firstLaneWithStreet.detectedStreetName.includes(" - ")) {
                    const parts = firstLaneWithStreet.detectedStreetName.split(" - ");
                    if (parts.length >= 2) {
                        streetName = parts[0].replace(/^\(.*?\)\s+/, "").trim();
                    }
                }

                if (streetName) {
                    directionStreetNames.set(dirGroup.direction, streetName);
                }
            }
        });

        Array.from(columnsByDirection.keys()).forEach((direction) => {
            if (!directionStreetNames.has(direction)) {
                const dirGroup = directionGroups.find((dg) => dg.direction === direction);
                if (dirGroup && dirGroup.groupedLaneLines.length > 0) {
                    for (const group of dirGroup.groupedLaneLines) {
                        for (const laneLine of group.laneLines) {
                            if (laneLine.name && laneLine.name.length > 0) {
                                directionStreetNames.set(direction, laneLine.name);
                                break;
                            }
                        }
                        if (directionStreetNames.has(direction)) break;
                    }
                }
            }
        });

        const vehicleTypeLabel = vehicleType === "all" ? "All Vehicles" : vehicleType === "heavy" ? "Heavy Vehicles" : vehicleType === "medium" ? "Medium Vehicles" : "Light Vehicles";

        const dataByDay = new Map<string, any[]>();
        reportData.counts?.forEach((row: any) => {
            if (row.startTime) {
                const date = new Date(row.startTime);
                const dayKey = date.toDateString();
                if (!dataByDay.has(dayKey)) {
                    dataByDay.set(dayKey, []);
                }
                dataByDay.get(dayKey)!.push(row);
            }
        });

        const intersectionName = getIntersectionName(directionStreetNames);
        const startDate = reportConfig.inclusiveStart;
        const endDate = reportConfig.exclusiveEnd;

        return (
            <div className="p-4">
                <div className="print:hidden w-full flex flex-row mb-4">
                    <div className="flex-grow">
                        <button onClick={() => { reportGeneratedRef.current = false; setReportData(null); }}>
                            <IconLink Icon={Icons.ArrowBack}>Go Back</IconLink>
                        </button>
                    </div>
                    <div>
                        <button onClick={() => downloadCSVV2(reportData, directionStreetNames, vehicleTypeLabel, startDate!, endDate!)}>
                            <IconLink Icon={Icons.Download}>Download CSV</IconLink>
                        </button>
                    </div>
                </div>

                <div className="text-center py-5">
                    {intersectionName && <h1 className="text-2xl font-bold">{intersectionName}</h1>}
                    <h2 className="text-xl">Turning Movement Count Report</h2>
                    <h3 className="text-gray-600">
                        {startDate?.toDateString()} through {endDate?.toDateString()}
                    </h3>
                </div>

                <div className="space-y-8">
                    {Array.from(dataByDay.entries()).map(([dayKey, dayRows]) => (
                        <div key={dayKey} className="bg-white border border-gray-300 rounded-lg p-6">
                            <div className="flex items-start justify-between mb-4">
                                <h2 className="text-xl font-bold">Traffic Counts - {vehicleTypeLabel}</h2>
                                <div className="text-right text-sm">
                                    {new Date(dayKey).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })}
                                </div>
                            </div>

                            {(() => {
                                const peakResult = calculateRollingPeakHour(dayRows, reportData.columns || [], getCountForColumn);
                                const peakStartHour = peakResult.peakStartTime?.getHours() ?? -1;

                                const hourlyData = new Map<number, { totals: Map<string, number>; grandTotal: number }>();

                                dayRows.forEach((row: any) => {
                                    if (!row.startTime) return;
                                    const hour = new Date(row.startTime).getHours();

                                    if (!hourlyData.has(hour)) {
                                        hourlyData.set(hour, { totals: new Map(), grandTotal: 0 });
                                    }

                                    const hourData = hourlyData.get(hour)!;
                                    reportData.columns?.forEach((col: any) => {
                                        const count = getCountForColumn(row, col.key);
                                        hourData.totals.set(col.key, (hourData.totals.get(col.key) || 0) + count);
                                        hourData.grandTotal += count;
                                    });
                                });

                                const selectedHour = selectedHourByDay.get(dayKey) ?? peakStartHour;
                                const selectedHourData = selectedHour >= 0 ? hourlyData.get(selectedHour) : null;
                                const hours = Array.from(hourlyData.keys()).sort((a, b) => a - b);

                                return (
                                    <div className="mb-6 border border-gray-300 rounded-lg p-4 bg-gray-50">
                                        <div className="mb-4 flex items-center justify-between print:hidden">
                                            <h3 className="text-lg font-semibold">Intersection Traffic Diagram</h3>
                                            <div className="flex items-center gap-3">
                                                <label className="text-sm font-medium">Select Hour:</label>
                                                <select
                                                    value={selectedHour}
                                                    onChange={(e) => {
                                                        const newMap = new Map(selectedHourByDay);
                                                        newMap.set(dayKey, parseInt(e.target.value));
                                                        setSelectedHourByDay(newMap);
                                                    }}
                                                    className="border border-gray-300 rounded px-3 py-1 text-sm"
                                                >
                                                    {hours.map((hour) => (
                                                        <option key={hour} value={hour}>
                                                            {String(hour).padStart(2, "0")}:00 - {String((hour + 1) % 24).padStart(2, "0")}:00
                                                            {hour === peakStartHour ? " (Peak Hour)" : ""}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <IntersectionDiagram
                                            reportColumns={reportData.columns || []}
                                            hourlyData={selectedHourData?.totals || new Map()}
                                            dailyTotals={(() => {
                                                const totals = new Map<string, number>();
                                                reportData.columns?.forEach((col: any) => {
                                                    const total = dayRows.reduce((sum: number, row: any) => {
                                                        const countObj = row.counts?.find((c: any) => c.columnKey === col.key);
                                                        return sum + (countObj?.count || 0);
                                                    }, 0);
                                                    totals.set(col.key, total);
                                                });
                                                return totals;
                                            })()}
                                            columnsByDirection={columnsByDirection}
                                            directionStreetNames={directionStreetNames}
                                        />
                                    </div>
                                );
                            })()}

                            <div>
                                <table className="text-sm mx-auto rounded-lg shadow border border-gray-200 mb-10">
                                    <thead className="bg-gray-100 sticky top-0 z-10">
                                        <tr className="bg-gray-50">
                                            <th rowSpan={3} className="border-l border-r border-t border-gray-300 px-4 py-2 text-sm font-semibold text-left align-middle bg-gray-50">
                                                Interval Start Time
                                            </th>
                                            {Array.from(columnsByDirection.entries()).map(([dir, cols]) => (
                                                <th key={dir} colSpan={cols.length} className="border-l border-r border-t border-gray-300 px-2 py-2 text-sm font-semibold text-center bg-gray-50">
                                                    {directionStreetNames.get(dir) || ""}
                                                </th>
                                            ))}
                                            <th rowSpan={3} className="border-l border-r border-t border-gray-300 px-4 py-2 text-sm font-semibold text-center align-middle bg-gray-200">
                                                Total
                                            </th>
                                        </tr>
                                        <tr className="bg-gray-50">
                                            {Array.from(columnsByDirection.entries()).map(([dir, cols]) => (
                                                <th key={dir} colSpan={cols.length} className="border-l border-r border-gray-300 px-2 py-2 text-sm font-semibold text-center bg-gray-50">
                                                    {getCardinalDirectionWithBound(dir)}
                                                </th>
                                            ))}
                                        </tr>
                                        <tr className="bg-gray-100 border-b border-gray-300">
                                            {reportData.columns?.map((col: any) => (
                                                <th key={col.key} className="border-l border-r border-gray-300 px-2 py-2 text-xs font-semibold text-center bg-gray-100">
                                                    {col.displayLabel}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dayRows.map((row: any, idx: number) => {
                                            const rowTotal = reportData.columns?.reduce((sum: number, col: any) => sum + getCountForColumn(row, col.key), 0) || 0;

                                            return (
                                                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                    <td className="border border-gray-300 px-4 py-2 text-sm whitespace-nowrap">
                                                        {row.startTime &&
                                                            new Date(row.startTime).toLocaleTimeString("en-US", {
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                                hour12: false,
                                                            })}
                                                    </td>
                                                    {reportData.columns?.map((col: any) => (
                                                        <td key={col.key} className="border border-gray-300 px-2 py-2 text-sm text-center">
                                                            {getCountForColumn(row, col.key)}
                                                        </td>
                                                    ))}
                                                    <td className="border border-gray-300 px-4 py-2 text-sm text-center font-semibold bg-gray-100">{rowTotal}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-200 font-bold">
                                            <td className="border border-gray-300 px-4 py-2 text-sm">Count Total</td>
                                            {reportData.columns?.map((col: any) => {
                                                const colTotal = dayRows.reduce((sum: number, row: any) => sum + getCountForColumn(row, col.key), 0);
                                                return (
                                                    <td key={col.key} className="border border-gray-300 px-2 py-2 text-sm text-center">
                                                        {colTotal}
                                                    </td>
                                                );
                                            })}
                                            <td className="border border-gray-300 px-4 py-2 text-sm text-center">
                                                {dayRows.reduce((sum: number, row: any) => {
                                                    return sum + reportData.columns?.reduce((rowSum: number, col: any) => rowSum + getCountForColumn(row, col.key), 0);
                                                }, 0)}
                                            </td>
                                        </tr>
                                        {(() => {
                                            const peakResult = calculateRollingPeakHour(dayRows, reportData.columns || [], getCountForColumn);
                                            const peakTimeLabel = formatPeakHourLabel(peakResult.peakStartTime, peakResult.peakEndTime);

                                            return (
                                                <tr className="bg-yellow-50 font-semibold">
                                                    <td className="border border-gray-300 px-4 py-2 text-sm">Peak Hour ({peakTimeLabel})</td>
                                                    {reportData.columns?.map((col: any) => {
                                                        const peakCount = peakResult.peakTotals.get(col.key) || 0;
                                                        return (
                                                            <td key={col.key} className="border border-gray-300 px-2 py-2 text-sm text-center">
                                                                {peakCount}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="border border-gray-300 px-4 py-2 text-sm text-center">{peakResult.peakTotal}</td>
                                                </tr>
                                            );
                                        })()}
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Configuration view (when no report data)
    return (
        <div className="p-4">
            <h2 className="text-2xl font-bold mb-4">Turning Movement Count Report</h2>
            <p className="text-gray-600 mb-4">Select the lane lines you want to include in the report. Lane numbers from your layout are used to identify each lane.</p>

            <div className="mb-6 border border-gray-300 rounded-lg p-4 bg-gray-50 print:hidden">
                <h3 className="text-lg font-semibold mb-4">Lane Lines Visualization</h3>
                <DynamicFixedSizeContainer getHeight={() => 600} getWidth={() => window.innerWidth - 300}>
                    {({ width, height }) => (
                        <div style={{ width, height }} className="flex flex-col">
                            <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
                                <OmniGoogleMap ref={mapRef} center={coordsHelper.toLatLng(new Vector2({ x: 0, y: 0 }))} height={height - 100} width={width}>
                                    {laneLines.map((laneLine) => (
                                        <LaneLineMapShape key={laneLine.id} facility={facility} shape={laneLine} isEditable={false} isSelectable={false} color={getLaneNumberColor(laneLine.laneNumber)} showLabel={true} />
                                    ))}
                                </OmniGoogleMap>
                            </div>

                            <div className="mt-3 p-3 bg-white border border-gray-300 rounded">
                                <h4 className="text-sm font-semibold mb-2">Lane Number Colors:</h4>
                                <div className="flex flex-wrap gap-4">
                                    {(() => {
                                        const uniqueLaneNumbers = Array.from(new Set(laneLines.map((ll) => ll.laneNumber).filter((num) => num !== null && num !== undefined))).sort((a, b) => a - b);

                                        return uniqueLaneNumbers.map((laneNum) => (
                                            <div key={laneNum} className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded border border-gray-400" style={{ backgroundColor: getLaneNumberColor(laneNum) }}></div>
                                                <span className="text-sm font-medium">Lane {laneNum}</span>
                                            </div>
                                        ));
                                    })()}
                                    {laneLines.some((ll) => ll.laneNumber === null || ll.laneNumber === undefined) && (
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded border border-gray-400" style={{ backgroundColor: getLaneNumberColor(null) }}></div>
                                            <span className="text-sm font-medium text-gray-600">Unassigned</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </DynamicFixedSizeContainer>
            </div>

            {validation.hasConflicts && (
                <div className="bg-red-50 border border-red-300 rounded p-4 mb-4">
                    <h3 className="text-red-800 font-semibold mb-3">
                        <i className="fa fa-exclamation-triangle mr-2"></i>
                        Lane Number Conflicts Detected
                    </h3>
                    <p className="text-red-700 text-sm mb-3">The same lane number is assigned to multiple directions. Please choose which direction to use for each conflicted lane number in the report.</p>
                    <div className="space-y-3">
                        {Array.from(validation.conflicts.entries()).map(([laneNum, directions]) => {
                            const directionOptions = validation.conflictDirectionOptions.get(laneNum) || [];
                            const selectedDirection = conflictResolutions.get(laneNum);

                            return (
                                <div key={laneNum} className="bg-white border border-red-200 rounded p-3">
                                    <div className="flex items-center gap-4">
                                        <span className="text-red-800 font-medium whitespace-nowrap">Lane {laneNum}:</span>
                                        <span className="text-red-700 text-sm flex-shrink">Appears in {directions.join(", ")}</span>
                                        <div className="flex items-center gap-2 ml-auto">
                                            <label className="text-red-700 text-sm whitespace-nowrap">Use direction:</label>
                                            <select
                                                value={selectedDirection ?? ""}
                                                onChange={(e) => {
                                                    const newResolutions = new Map(conflictResolutions);
                                                    if (e.target.value) {
                                                        newResolutions.set(laneNum, parseInt(e.target.value) as DetectionZoneCardinalDirection);
                                                    } else {
                                                        newResolutions.delete(laneNum);
                                                    }
                                                    setConflictResolutions(newResolutions);
                                                }}
                                                className="border border-red-300 rounded px-3 py-1 text-sm bg-white"
                                            >
                                                <option value="">-- Select Direction --</option>
                                                {directionOptions.map((dir) => (
                                                    <option key={dir} value={dir}>
                                                        {getCardinalDirectionAbbreviation(dir)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-red-700 text-sm mt-3">
                        <i className="fa fa-lightbulb-o mr-1"></i>
                        Tip: To fix this, go to the layout editor and assign unique lane numbers to each lane line main direction.
                    </p>
                </div>
            )}

            {validation.hasUnassigned && (
                <div className="bg-red-50 border border-red-300 rounded p-4 mb-4">
                    <h3 className="text-red-800 font-semibold mb-2">
                        <i className="fa fa-exclamation-circle mr-2"></i>
                        Lane Lines Without Numbers
                    </h3>
                    <p className="text-red-700 text-sm mb-2">{validation.unassignedGroups.length} lane line group(s) don't have lane numbers assigned. These lanes won't track any targets.</p>
                    <p className="text-red-700 text-sm">
                        <i className="fa fa-lightbulb-o mr-1"></i>
                        Fix: Go to the layout designer and assign lane numbers to all lane lines you want to track.
                    </p>
                </div>
            )}

            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type Filter</label>
                <div className="max-w-xs">
                    <Select
                        value={vehicleType}
                        onChange={(e) => setVehicleType(e.target.value as VehicleType)}
                        options={[
                            { value: "all", label: "All Vehicles" },
                            { value: "heavy", label: "Heavy Vehicles" },
                            { value: "medium", label: "Medium Vehicles" },
                            { value: "light", label: "Light Vehicles" },
                        ]}
                    />
                </div>
            </div>

            <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showStreetNames} onChange={(e) => setShowStreetNames(e.target.checked)} className="cursor-pointer h-4 w-4" disabled={isDetectingStreetNames} />
                    <span className="text-sm font-medium text-gray-700">
                        Show Detected Street Names
                        {isDetectingStreetNames && <i className="fa fa-spinner fa-spin ml-2"></i>}
                    </span>
                </label>
                <p className="text-xs text-gray-500 ml-6 mt-1">Display auto-detected street names based on lane geometry instead of custom lane names</p>
            </div>

            {isDetectingStreetNames && (
                <div className="mb-4 bg-blue-50 border border-blue-300 rounded p-4">
                    <div className="flex items-center">
                        <i className="fa fa-spinner fa-spin text-blue-600 text-xl mr-3"></i>
                        <div>
                            <p className="text-blue-800 font-medium">Detecting Street Names...</p>
                            <p className="text-blue-600 text-sm">Geocoding lane line coordinates to find street names. This may take a few moments.</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-6">
                {directionGroups.map((dirGroup) => {
                    const allDirIds: string[] = [];
                    dirGroup.groupedLaneLines.forEach((group) => {
                        group.laneLines.forEach((ll) => {
                            if (ll.id) allDirIds.push(ll.id);
                        });
                    });

                    const allDirSelected = allDirIds.every((id) => selectedLaneLineIds.has(id));
                    const someDirSelected = allDirIds.some((id) => selectedLaneLineIds.has(id));

                    return (
                        <div key={dirGroup.direction} className="border rounded-lg p-4 bg-white shadow-sm">
                            <div className="flex items-center mb-3">
                                <input
                                    type="checkbox"
                                    checked={allDirSelected}
                                    ref={(input) => {
                                        if (input) {
                                            input.indeterminate = someDirSelected && !allDirSelected;
                                        }
                                    }}
                                    onChange={() => toggleDirection(dirGroup.direction)}
                                    className="mr-3 h-5 w-5 cursor-pointer"
                                />
                                <h3 className="text-lg font-semibold">
                                    <i className="fa fa-compass mr-2"></i>
                                    {getCardinalDirectionWithBound(dirGroup.direction)}
                                    <span className="text-sm text-gray-500 ml-2">
                                        ({dirGroup.groupedLaneLines.length} unique lane{dirGroup.groupedLaneLines.length !== 1 ? "s" : ""})
                                    </span>
                                </h3>
                            </div>

                            <div className="ml-8 space-y-2">
                                {dirGroup.groupedLaneLines.map((group, idx) => {
                                    const isConflicted = group.laneNumber !== 0 && validation.conflicts.has(group.laneNumber);
                                    const isUnassigned = group.laneNumber === 0;

                                    const chosenDirection = conflictResolutions.get(group.laneNumber);
                                    const isExcludedByConflictResolution = isConflicted && chosenDirection !== undefined && chosenDirection !== dirGroup.direction;

                                    const groupIds = group.laneLines.map((ll) => ll.id).filter((id) => id) as string[];
                                    const allGroupSelected = groupIds.every((id) => selectedLaneLineIds.has(id));
                                    const displayName = showStreetNames && group.detectedStreetName ? group.detectedStreetName : group.displayName;

                                    return (
                                        <label key={`${group.laneNumber}-${group.turnType}-${idx}`} className={`flex items-center cursor-pointer p-2 rounded ${isExcludedByConflictResolution ? "bg-gray-100 opacity-50 cursor-not-allowed" : isConflicted || isUnassigned ? "bg-red-50 hover:bg-red-100 border border-red-200" : "hover:bg-gray-50"}`}>
                                            <input type="checkbox" checked={allGroupSelected} onChange={() => toggleGroup(group)} disabled={isExcludedByConflictResolution} className="mr-3 h-4 w-4 cursor-pointer" />
                                            <span className={`font-medium mr-2 ${isConflicted || isUnassigned ? "text-red-700" : "text-gray-700"}`}>
                                                {isConflicted && <i className="fa fa-exclamation-triangle mr-1"></i>}
                                                {isUnassigned ? "No Lane #" : `Lane ${group.laneNumber}`}:
                                            </span>
                                            <span className="text-gray-600 flex-1">{displayName}</span>
                                            {group.laneLines.length > 1 && <span className="text-xs text-gray-500 ml-2">({group.laneLines.length} lane lines merged)</span>}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 flex gap-4 items-center">
                <Button onClick={handleGenerateReport} disabled={selectedLaneLineIds.size === 0 || (validation.hasConflicts && conflictResolutions.size !== validation.conflicts.size)} className="px-6 py-3">
                    <i className="fa fa-chart-bar mr-2"></i>
                    Generate TMC Report
                </Button>

                <div className="text-sm text-gray-600 flex items-center">
                    {selectedLaneLineIds.size} lane line{selectedLaneLineIds.size !== 1 ? "s" : ""} selected
                </div>

                {validation.hasConflicts && conflictResolutions.size !== validation.conflicts.size && (
                    <div className="text-sm text-red-700 flex items-center">
                        <i className="fa fa-exclamation-circle mr-1"></i>
                        Please resolve all lane conflicts to generate report
                    </div>
                )}
            </div>

            {isLoadingReport && (
                <div className="mt-6 p-8 bg-gray-50 rounded border border-gray-200 text-center">
                    <i className="fa fa-spinner fa-spin text-3xl text-gray-400 mb-4"></i>
                    <p className="text-gray-600">Generating report...</p>
                </div>
            )}

            {reportError && (
                <div className="mt-6 bg-red-50 border border-red-300 rounded p-4">
                    <h3 className="text-red-800 font-semibold mb-2">
                        <i className="fa fa-exclamation-circle mr-2"></i>
                        Error Generating Report
                    </h3>
                    <p className="text-red-700">{reportError}</p>
                </div>
            )}
        </div>
    );
};

export const TurningMovementReport = {
    name: "Turning Movement Count Report",
    description: "Turning movement count report with automatic direction detection from lane line geometry.",
    component: TMCReportLoader,
    key: "tmc",
    defaultChartType: "other",
    defaultRange: DateOptions.LastWeek,
    config: {
        requiresMonth: false,
        allowsCustomRange: true,
        requiresYear: false,
        allowTargetTypeGrouping: false,
        maxDaysRange: 31, // Backend enforces 31-day limit for TMC reports
    },
} as IConfiguredTrafficReport;
